import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef } from "react";
import { Form, Input, Select, Switch, InputNumber, DatePicker, Radio, Divider, TreeSelect, Row, Col, ColorPicker, } from "antd";
// 使用 Form.Provider 作为 FormProvider
const FormProvider = Form.Provider;
import dayjs from "dayjs";
const { Option } = Select;
const { TextArea } = Input;
const { Group: RadioGroup } = Radio;
/**
 * 通用表单组件（内核）
 * 只包含表单核心功能，不包含容器和标题栏
 */
const CompsForm = ({ formItems, formRef, initialValues = {}, onValuesChange, formProps, layout = "vertical", // 默认为垂直布局
labelCol = { span: 6 }, // 默认标签宽度
wrapperCol = { span: 18 }, // 默认内容区域宽度
validateMessages, className, style, contentPadding = 16, gutter = [16, 8], // 默认水平间距16，垂直间距8
noWrapper = false, noRowCol = false, }) => {
    const [form] = Form.useForm();
    const lastInitialValuesRef = useRef("");
    const resetCountRef = useRef(0);
    // 将表单实例传递给父组件
    useEffect(() => {
        if (formRef && form) {
            formRef(form);
        }
    }, [form, formRef]);
    // 当 initialValues 变化时重置表单
    useEffect(() => {
        if (form) {
            const currentInitialValues = JSON.stringify(initialValues);
            // 检查是否真的需要重置
            if (lastInitialValuesRef.current === currentInitialValues) {
                return; // 初始值没有变化，跳过重置
            }
            // 如果用户已经开始编辑，则不因父组件状态变化而重置，避免输入被打断
            // 仅在首次或未触碰表单时根据 initialValues 同步
            const hasTouched = form.isFieldsTouched(true);
            if (hasTouched && lastInitialValuesRef.current) {
                return;
            }
            // 渲染计数防护
            resetCountRef.current += 1;
            if (resetCountRef.current > 5) {
                console.warn("⚠️ CompsForm 重置次数过多，可能存在循环重置");
                return;
            }
            lastInitialValuesRef.current = currentInitialValues;
            // 先重置表单所有字段
            form.resetFields();
            // 如果有初始值，设置表单值
            if (Object.keys(initialValues).length > 0) {
                // 应用 transform.in 转换初始值
                const transformedValues = {};
                Object.keys(initialValues).forEach((key) => {
                    const item = formItems.find((item) => item.name === key);
                    const value = initialValues[key];
                    // 对于日期字段，即使没有 transform，也要尝试转换
                    if (item?.type === "date" && value !== undefined && value !== null) {
                        // 如果已经是 dayjs 对象，直接使用
                        if (dayjs.isDayjs(value)) {
                            transformedValues[key] = value;
                        }
                        else if (item?.transform?.in) {
                            try {
                                const transformed = item.transform.in(value);
                                // 确保转换后的值是有效的 dayjs 对象
                                if (transformed &&
                                    (dayjs.isDayjs(transformed) ||
                                        typeof transformed?.isValid === "function")) {
                                    transformedValues[key] = transformed;
                                }
                                else {
                                    // 如果 transform 失败，尝试直接使用 dayjs 转换
                                    try {
                                        const dayjsValue = dayjs(value);
                                        if (dayjsValue.isValid()) {
                                            transformedValues[key] = dayjsValue;
                                        }
                                        else {
                                            console.warn(`日期初始值转换失败 [${key}]: ${value}, transformed:`, transformed);
                                            transformedValues[key] = undefined;
                                        }
                                    }
                                    catch (e) {
                                        console.warn(`日期初始值转换失败 [${key}]: ${value}, transformed:`, transformed);
                                        transformedValues[key] = undefined;
                                    }
                                }
                            }
                            catch (error) {
                                // 如果 transform 异常，尝试直接使用 dayjs 转换
                                try {
                                    const dayjsValue = dayjs(value);
                                    if (dayjsValue.isValid()) {
                                        transformedValues[key] = dayjsValue;
                                    }
                                    else {
                                        console.warn(`日期初始值转换异常 [${key}]: ${value}`, error);
                                        transformedValues[key] = undefined;
                                    }
                                }
                                catch (e) {
                                    console.warn(`日期初始值转换异常 [${key}]: ${value}`, error);
                                    transformedValues[key] = undefined;
                                }
                            }
                        }
                        else {
                            // 如果没有 transform，尝试直接转换
                            try {
                                const date = dayjs(value);
                                if (date.isValid()) {
                                    transformedValues[key] = date;
                                }
                                else {
                                    transformedValues[key] = undefined;
                                }
                            }
                            catch (error) {
                                console.warn(`日期初始值直接转换失败 [${key}]: ${value}`, error);
                                transformedValues[key] = undefined;
                            }
                        }
                    }
                    else if (item?.transform?.in &&
                        value !== undefined &&
                        value !== null) {
                        try {
                            transformedValues[key] = item.transform.in(value);
                        }
                        catch (error) {
                            console.warn(`初始值转换失败 [${key}]: ${value}`, error);
                            transformedValues[key] = value;
                        }
                    }
                    else {
                        transformedValues[key] = value;
                    }
                });
                console.log("🔍 CompsForm 设置表单初始值:", JSON.stringify({
                    initialValues,
                    transformedValues,
                    timestamp: new Date().toISOString(),
                    callStack: new Error().stack
                        ?.split("\n")
                        .slice(1, 4)
                        .map((line) => line.trim()),
                }, null, 2));
                form.setFieldsValue(transformedValues);
                // 确保所有日期字段的值都是 dayjs 对象
                // 通过 getFieldsValue 和 setFieldsValue 触发 normalize
                setTimeout(() => {
                    const currentValues = form.getFieldsValue();
                    const dateFields = formItems.filter((item) => item.type === "date");
                    let needsUpdate = false;
                    const updatedValues = {};
                    dateFields.forEach((item) => {
                        if (item.name && currentValues[item.name] !== undefined) {
                            const value = currentValues[item.name];
                            // 如果不是 dayjs 对象，尝试转换
                            if (!dayjs.isDayjs(value) &&
                                value !== null &&
                                value !== undefined) {
                                try {
                                    const dayjsValue = dayjs(value);
                                    if (dayjsValue.isValid()) {
                                        updatedValues[item.name] = dayjsValue;
                                        needsUpdate = true;
                                    }
                                }
                                catch (e) {
                                    // 忽略错误
                                }
                            }
                        }
                    });
                    if (needsUpdate) {
                        form.setFieldsValue(updatedValues);
                    }
                }, 0);
            }
            else {
                // 确保表单完全清空
                const emptyValues = {};
                formItems.forEach((item) => {
                    if (typeof item.name === "string") {
                        emptyValues[item.name] = undefined;
                    }
                });
                console.log("🔍 CompsForm 清空表单值:", JSON.stringify({
                    emptyValues,
                    timestamp: new Date().toISOString(),
                    callStack: new Error().stack
                        ?.split("\n")
                        .slice(1, 4)
                        .map((line) => line.trim()),
                }, null, 2));
                form.setFieldsValue(emptyValues);
            }
            if (process.env.NODE_ENV === "development") {
                const logLevel = localStorage.getItem("arraySelectorLogLevel");
                if (logLevel && parseInt(logLevel, 10) >= 4) {
                    console.log("CompsForm 表单已重置, 初始值:", initialValues);
                }
            }
        }
    }, [initialValues, form, formItems]);
    // 定期重置计数，避免长期累积
    useEffect(() => {
        const resetTimer = setInterval(() => {
            resetCountRef.current = 0;
        }, 3000); // 每3秒重置一次
        return () => {
            clearInterval(resetTimer);
        };
    }, []);
    // 渲染表单控件
    const renderFormItem = (item) => {
        const { type, options, props = {} } = item;
        switch (type) {
            case "input":
                return (_jsx(Input, { size: props.size ?? "middle", style: { width: "100%", ...(props.style || {}) }, ...props }));
            case "textarea":
                return _jsx(TextArea, { rows: 4, ...props });
            case "select":
                return (_jsx(Select, { size: props.size ?? "middle", style: { width: "100%", ...(props.style || {}) }, ...props, children: options?.map((opt, index) => (_jsx(Option
                    // 注意：0 是合法值，不能用 `||` 否则会被当成空值
                    , { value: (opt.value ?? ""), disabled: opt.disabled, children: opt.label }, (opt.value ?? `option-${index}`)))) }));
            case "treeSelect":
                return (_jsx(TreeSelect, { showSearch: true, style: { width: "100%" }, styles: { popup: { root: { maxHeight: 400, overflow: "auto" } } }, placeholder: "\u8BF7\u9009\u62E9", allowClear: true, treeDefaultExpandAll: true, treeNodeFilterProp: "title", size: props.size ?? "middle", ...props }));
            case "switch":
                return _jsx(Switch, { ...props });
            case "number":
                return (_jsx(InputNumber, { size: props.size ?? "middle", style: { width: "100%", ...(props.style || {}) }, ...props }));
            case "date":
                return (_jsx(DatePicker, { size: props.size ?? "middle", style: { width: "100%", ...(props.style || {}) }, ...props }));
            case "radio":
                return (_jsx(RadioGroup, { ...props, children: options?.map((opt) => (_jsx(Radio, { value: opt.value, children: opt.label }, opt.value))) }));
            case "colorPicker":
                return (_jsx(ColorPicker, { ...props, showText: true, format: "hex" }));
            case "custom":
                const CustomComponent = item.customComponent;
                return CustomComponent ? (_jsx(CustomComponent, { ...props })) : (_jsx(Input, { ...props }));
            default:
                return _jsx(Input, { ...props });
        }
    };
    // 根据布局模式准备表单属性
    const layoutFormProps = {
        layout,
        ...(layout === "horizontal" ? { labelCol, wrapperCol } : {}),
    };
    // 构建内容样式
    const contentStyle = {
        padding: typeof contentPadding === "number"
            ? `${contentPadding}px`
            : contentPadding,
        ...style,
    };
    const wrapItem = (key, colSpan, content) => noRowCol ? (_jsx(React.Fragment, { children: content }, key)) : (_jsx(Col, { span: colSpan, children: content }, key));
    const formItemsContent = formItems.map((item) => {
        // 处理分隔线类型
        if (item.type === "divider") {
            return wrapItem(item.name, 24, _jsx(Divider, { orientation: "left", style: { marginTop: 24, marginBottom: 16 }, children: _jsx("span", { style: { fontWeight: "bold" }, children: item.label }) }));
        }
        // 处理普通表单项 - 使用 colSpan，默认为 24（整行）
        const colSpan = item.colSpan || 24;
        const itemFormLayoutProps = item.labelCol != null || item.wrapperCol != null
            ? {
                ...(item.labelCol != null ? { labelCol: item.labelCol } : {}),
                ...(item.wrapperCol != null ? { wrapperCol: item.wrapperCol } : {}),
            }
            : {};
        // 如果字段有dependencies，需要使用Form.Item的shouldUpdate来动态更新
        if (item.dependencies && item.dependencies.length > 0) {
            return wrapItem(item.name, colSpan, _jsx(Form.Item, { noStyle: true, shouldUpdate: (prevValues, currentValues) => {
                    // 检查依赖字段是否有变化
                    return (item.dependencies?.some((dep) => prevValues[dep] !== currentValues[dep]) || false);
                }, children: ({ getFieldValue }) => {
                    // 支持 hidden 为函数：在 dependencies 更新时重新判断是否隐藏
                    if (typeof item.hidden === "function") {
                        try {
                            const allValues = { ...(initialValues || {}) };
                            const deps = item.dependencies || [];
                            for (const dep of deps) {
                                allValues[dep] = getFieldValue(dep);
                            }
                            const shouldHide = item.hidden(allValues);
                            if (shouldHide)
                                return null;
                        }
                        catch (e) {
                            // ignore hidden evaluation errors, fall back to render
                        }
                    }
                    else if (item.hidden === true) {
                        return null;
                    }
                    // 根据依赖字段的值动态设置disabled
                    let disabled = item.props?.disabled;
                    if (item.dependencies?.includes("is_active")) {
                        const isActive = getFieldValue("is_active");
                        disabled = isActive === false;
                    }
                    // 收集依赖字段的值，传递给custom组件
                    const dependencyValues = {};
                    if (item.dependencies && item.dependencies.length > 0) {
                        item.dependencies.forEach((dep) => {
                            dependencyValues[dep] = getFieldValue(dep);
                        });
                    }
                    const itemWithDisabled = {
                        ...item,
                        props: {
                            ...item.props,
                            disabled,
                            // 将依赖字段的值作为props传递给custom组件
                            ...(item.type === "custom" ? dependencyValues : {}),
                        },
                    };
                    return (_jsx(Form.Item, { name: item.name, label: item.label, rules: item.rules, ...itemFormLayoutProps, valuePropName: item.type === "switch" ? "checked" : "value", hidden: item.hidden === true, dependencies: item.dependencies, getValueProps: item.type === "date"
                            ? (value) => {
                                // 确保传递给 DatePicker 的值始终是 dayjs 对象
                                if (!value)
                                    return { value: undefined };
                                if (dayjs.isDayjs(value))
                                    return { value };
                                // 如果不是 dayjs 对象，尝试转换
                                try {
                                    const dayjsValue = dayjs(value);
                                    return dayjsValue.isValid()
                                        ? { value: dayjsValue }
                                        : { value: undefined };
                                }
                                catch (error) {
                                    console.warn(`日期 getValueProps 转换失败: ${value}`, error);
                                    return { value: undefined };
                                }
                            }
                            : undefined, getValueFromEvent: item.type === "date"
                            ? (value) => {
                                // 确保 DatePicker 返回的值始终是 dayjs 对象
                                if (!value)
                                    return undefined;
                                // DatePicker 应该返回 dayjs 对象，但为了安全起见，我们检查并转换
                                if (dayjs.isDayjs(value))
                                    return value;
                                // 如果不是 dayjs 对象，尝试转换
                                try {
                                    const dayjsValue = dayjs(value);
                                    return dayjsValue.isValid()
                                        ? dayjsValue
                                        : undefined;
                                }
                                catch (error) {
                                    console.warn(`日期 getValueFromEvent 转换失败: ${value}`, error);
                                    return undefined;
                                }
                            }
                            : item.type === "colorPicker"
                                ? (color) => {
                                    // ColorPicker 返回 Color 对象，需要转换为 hex 字符串
                                    if (!color)
                                        return undefined;
                                    try {
                                        return color.toHexString();
                                    }
                                    catch (error) {
                                        console.warn(`颜色 getValueFromEvent 转换失败: ${color}`, error);
                                        return undefined;
                                    }
                                }
                                : undefined, normalize: item.type === "date" || item.transform?.in
                            ? (value) => {
                                // normalize 在值设置到表单时调用（包括初始值和程序化设置的值）
                                if (value === undefined || value === null)
                                    return value;
                                // 检查是否已经是 dayjs 对象（对于日期字段）
                                if (item.type === "date") {
                                    // 如果已经是 dayjs 对象，直接返回
                                    if (value && dayjs.isDayjs(value)) {
                                        return value;
                                    }
                                    // 对于日期字段，必须确保值是 dayjs 对象
                                    // 即使值有 isValid 方法，如果不是 dayjs 对象，也要转换
                                    // 否则尝试转换
                                    if (item.transform?.in) {
                                        try {
                                            const transformed = item.transform.in(value);
                                            // 确保转换后的值是有效的 dayjs 对象
                                            if (transformed &&
                                                (dayjs.isDayjs(transformed) ||
                                                    typeof transformed?.isValid ===
                                                        "function")) {
                                                return transformed;
                                            }
                                            // 如果转换失败，尝试直接使用 dayjs 转换
                                            try {
                                                const dayjsValue = dayjs(value);
                                                if (dayjsValue.isValid()) {
                                                    return dayjsValue;
                                                }
                                            }
                                            catch (e) {
                                                // 忽略错误
                                            }
                                            console.warn(`日期 normalize 转换失败: ${value}`, transformed);
                                            return undefined;
                                        }
                                        catch (error) {
                                            // 如果 transform 失败，尝试直接使用 dayjs 转换
                                            try {
                                                const dayjsValue = dayjs(value);
                                                if (dayjsValue.isValid()) {
                                                    return dayjsValue;
                                                }
                                            }
                                            catch (e) {
                                                // 忽略错误
                                            }
                                            console.warn(`日期 normalize 转换异常: ${value}`, error);
                                            return undefined;
                                        }
                                    }
                                    else {
                                        // 如果没有 transform，尝试直接使用 dayjs 转换
                                        try {
                                            const dayjsValue = dayjs(value);
                                            if (dayjsValue.isValid()) {
                                                return dayjsValue;
                                            }
                                        }
                                        catch (error) {
                                            console.warn(`日期 normalize 直接转换失败: ${value}`, error);
                                        }
                                        return undefined;
                                    }
                                }
                                return item.transform?.in
                                    ? item.transform.in(value)
                                    : value;
                            }
                            : undefined, children: renderFormItem(itemWithDisabled) }));
                } }));
        }
        // 处理函数形式的 hidden 属性
        if (typeof item.hidden === "function") {
            // 需要依赖的字段列表（包括 hidden 函数可能使用的字段）
            const allDependencies = item.dependencies || [];
            return wrapItem(item.name, colSpan, _jsx(Form.Item, { noStyle: true, dependencies: allDependencies, children: ({ getFieldsValue, getFieldValue }) => {
                    // 构建包含所有字段值的对象
                    // 首先从 getFieldsValue() 获取所有值
                    const formValues = getFieldsValue() || {};
                    // 然后为每个依赖字段单独获取值（这确保即使 getFieldsValue() 为空也能获取到值）
                    const allValues = { ...initialValues };
                    // 合并表单中的所有值
                    Object.keys(formValues).forEach((key) => {
                        if (formValues[key] !== undefined) {
                            allValues[key] = formValues[key];
                        }
                    });
                    // 确保所有依赖字段的值都被包含（优先使用 getFieldValue，因为它能获取到最新的值）
                    allDependencies.forEach((dep) => {
                        const fieldValue = getFieldValue(dep);
                        if (fieldValue !== undefined) {
                            allValues[dep] = fieldValue;
                        }
                        else if (initialValues[dep] !== undefined) {
                            allValues[dep] = initialValues[dep];
                        }
                    });
                    // 调试日志
                    if (process.env.NODE_ENV === "development") {
                        console.log(`[CompsForm] Field ${Array.isArray(item.name) ? item.name.join('.') : item.name}:`, {
                            allValues,
                            dependencies: allDependencies,
                            formValues,
                            initialValues,
                            shouldHide: typeof item.hidden === 'function' ? item.hidden(allValues) : (item.hidden || false),
                        });
                    }
                    const shouldHide = typeof item.hidden === 'function' ? item.hidden(allValues) : (item.hidden || false);
                    if (shouldHide)
                        return null;
                    return (_jsx(Form.Item, { name: item.name, label: item.label, rules: item.rules, ...itemFormLayoutProps, valuePropName: item.type === "switch" ? "checked" : "value", children: renderFormItem(item) }));
                } }));
        }
        // 处理普通 hidden 属性
        const isHidden = item.hidden === true;
        return wrapItem(item.name, colSpan, _jsx(Form.Item, { name: item.name, label: item.label, rules: item.rules, ...itemFormLayoutProps, valuePropName: item.type === "switch" ? "checked" : "value", hidden: isHidden, dependencies: item.dependencies, getValueProps: item.type === "date"
                ? (value) => {
                    // 确保传递给 DatePicker 的值始终是 dayjs 对象
                    if (!value)
                        return { value: undefined };
                    if (dayjs.isDayjs(value))
                        return { value };
                    // 如果不是 dayjs 对象，尝试转换
                    try {
                        const dayjsValue = dayjs(value);
                        return dayjsValue.isValid()
                            ? { value: dayjsValue }
                            : { value: undefined };
                    }
                    catch (error) {
                        console.warn(`日期 getValueProps 转换失败 [${item.name}]: ${value}`, error);
                        return { value: undefined };
                    }
                }
                : undefined, getValueFromEvent: item.type === "date"
                ? (value) => {
                    // 确保 DatePicker 返回的值始终是 dayjs 对象
                    if (!value)
                        return undefined;
                    // DatePicker 应该返回 dayjs 对象，但为了安全起见，我们检查并转换
                    if (dayjs.isDayjs(value))
                        return value;
                    // 如果不是 dayjs 对象，尝试转换
                    try {
                        const dayjsValue = dayjs(value);
                        return dayjsValue.isValid()
                            ? dayjsValue
                            : undefined;
                    }
                    catch (error) {
                        console.warn(`日期 getValueFromEvent 转换失败 [${item.name}]: ${value}`, error);
                        return undefined;
                    }
                }
                : item.type === "colorPicker"
                    ? (color) => {
                        // ColorPicker 返回 Color 对象，需要转换为 hex 字符串
                        if (!color)
                            return undefined;
                        try {
                            return color.toHexString();
                        }
                        catch (error) {
                            console.warn(`颜色 getValueFromEvent 转换失败 [${item.name}]: ${color}`, error);
                            return undefined;
                        }
                    }
                    : undefined, normalize: item.type === "date" || item.transform?.in
                ? (value) => {
                    // normalize 在值设置到表单时调用（包括初始值和程序化设置的值）
                    // 这是确保日期值正确转换的关键位置
                    if (value === undefined || value === null) {
                        return value;
                    }
                    // 对于日期字段，确保值始终是 dayjs 对象
                    if (item.type === "date") {
                        // 如果已经是 dayjs 对象，直接返回
                        if (value && dayjs.isDayjs(value)) {
                            return value;
                        }
                        // 对于日期字段，必须确保值是 dayjs 对象
                        // 即使值有 isValid 方法，如果不是 dayjs 对象，也要转换
                        // 否则尝试转换（可能是字符串或其他格式）
                        if (item.transform?.in) {
                            try {
                                const transformed = item.transform.in(value);
                                // 确保转换后的值是有效的 dayjs 对象
                                if (transformed &&
                                    (dayjs.isDayjs(transformed) ||
                                        typeof transformed?.isValid === "function")) {
                                    return transformed;
                                }
                                // 如果转换失败，尝试直接使用 dayjs 转换
                                try {
                                    const dayjsValue = dayjs(value);
                                    if (dayjsValue.isValid()) {
                                        return dayjsValue;
                                    }
                                }
                                catch (e) {
                                    // 忽略错误，继续处理
                                }
                                // 如果转换失败，返回 undefined 而不是原始值
                                console.warn(`日期 normalize 转换失败 [${item.name}]: ${value}, transformed:`, transformed);
                                return undefined;
                            }
                            catch (error) {
                                // 如果 transform 失败，尝试直接使用 dayjs 转换
                                try {
                                    const dayjsValue = dayjs(value);
                                    if (dayjsValue.isValid()) {
                                        return dayjsValue;
                                    }
                                }
                                catch (e) {
                                    // 忽略错误
                                }
                                console.warn(`日期 normalize 转换异常 [${item.name}]: ${value}`, error);
                                return undefined;
                            }
                        }
                        else {
                            // 如果没有 transform，尝试直接使用 dayjs 转换
                            try {
                                const dayjsValue = dayjs(value);
                                if (dayjsValue.isValid()) {
                                    return dayjsValue;
                                }
                            }
                            catch (error) {
                                console.warn(`日期 normalize 直接转换失败 [${item.name}]: ${value}`, error);
                            }
                            return undefined;
                        }
                    }
                    // 非日期字段的正常转换
                    if (item.transform?.in) {
                        try {
                            return item.transform.in(value);
                        }
                        catch (error) {
                            console.warn(`字段 normalize 转换失败 [${item.name}]: ${value}`, error);
                            return value;
                        }
                    }
                    return value;
                }
                : undefined, children: renderFormItem(item) }));
    });
    const formBody = noRowCol ? (formItemsContent) : (_jsx(Row, { gutter: gutter, children: formItemsContent }));
    const formElement = (_jsx(FormProvider, { children: _jsx(Form, { form: form, initialValues: initialValues, validateMessages: validateMessages, autoComplete: "off", onValuesChange: (changedValues, allValues) => {
                console.log("🔍 Form onValuesChange 被触发:", JSON.stringify({
                    changedValues,
                    allValues,
                    timestamp: new Date().toISOString(),
                    callStack: new Error().stack
                        ?.split("\n")
                        .slice(1, 4)
                        .map((line) => line.trim()),
                }, null, 2));
                if (onValuesChange) {
                    onValuesChange(changedValues, allValues);
                }
            }, ...layoutFormProps, ...formProps, ...(noWrapper && { style: contentStyle, className }), children: formBody }) }));
    return noWrapper ? (formElement) : (_jsx("div", { className: className, style: contentStyle, children: formElement }));
};
export default CompsForm;
